import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import {
    useMaterialReactTable,
    MaterialReactTable,
} from "material-react-table";
import {
    MDBContainer,
    MDBRow,
    MDBCol,
    MDBCard,
    MDBCardBody,
    MDBCardTitle,
    MDBInput,
} from "mdb-react-ui-kit";
import { Button, COLORS } from "abs-ui-components";
import {
    getFailedPublishDocuments,
    retryPublishDocument,
} from "../../../services/externalPublishingService";
import { emitToasterMsg } from "../../../services/toasterUtility";
import "./ExternalPublishingTracker.scss";
import { ClaimsContext } from "../../../services/ClaimsService";
import { FeatureType } from "../../../constants/FeatureToggles";

const DEFAULT_PAGE_SIZE = 10;

const ExternalPublishingTracker = () => {
    const [documents, setDocuments] = useState([]);
    const [page, setPage] = useState(1);
    const [pageSize] = useState(DEFAULT_PAGE_SIZE);
    const [reportMstId, setReportMstId] = useState("");
    const [documentMstId, setDocumentMstId] = useState("");
    const [loading, setLoading] = useState(false);
    const [retrying, setRetrying] = useState(() => new Set());
    const [pagination, setPagination] = useState(null);

    const buildRequest = useCallback(
        (pageOverride) => ({
            Page: pageOverride ?? page,
            PageSize: pageSize,
            ReportMstId: reportMstId ? Number(reportMstId) : null,
            DocumentMstId: documentMstId ? Number(documentMstId) : null,
        }),
        [page, pageSize, reportMstId, documentMstId]
    );

    const fetchDocuments = useCallback(
        async (pageOverride) => {
            setLoading(true);
            try {
                const res = await getFailedPublishDocuments(buildRequest(pageOverride));
                const payload = res?.data?.data ?? {};
                const list = Array.isArray(payload?.data) ? payload.data : [];

                const mapped = list.map((item) => ({
                    ...item,
                    label: "Report Documents",
                }));
                setDocuments(mapped);
                setPagination(payload?.pagination ?? null);
            } catch (err) {
                console.error("Error fetching failed publish documents:", err);
                emitToasterMsg("error", "Documents", "Failed to load documents.");
                setDocuments([]);
            } finally {
                setLoading(false);
            }
        },
        [buildRequest]
    );

    useEffect(() => {
        fetchDocuments(page);
    }, [page]);

    const handleSearch = () => {
        setPage(1);
        fetchDocuments(1);
    };

    const handleClear = () => {
        setReportMstId("");
        setDocumentMstId("");
        setPage(1);
        fetchDocuments(1);
    };

    const handleRetry = async (doc) => {
        const erpId = doc.erpDocumentId ?? doc.ErpDocumentId;
        try {
            setRetrying((prev) => {
                const next = new Set(prev);
                next.add(erpId);
                return next;
            });
            await retryPublishDocument({
                ErpDocumentId: erpId,
                ReportMstId: doc.reportMstId ?? doc.ReportMstId,
            });
            emitToasterMsg("success", "Documents", "Retry publishing triggered.");
            await fetchDocuments(page);
        } catch (err) {
            console.error("Error retrying publish:", err);
            emitToasterMsg("error", "Documents", "Failed to retry publishing.");
        } finally {
            setRetrying((prev) => {
                const next = new Set(prev);
                next.delete(erpId);
                return next;
            });
        }
    };

    const handlePrev = () => {
        if (page > 1) setPage((p) => p - 1);
    };

    const handleNext = () => {
        setPage((p) => p + 1);
    };

    const formatDate = (value) => {
        if (!value) return "-";
        const d = new Date(value);
        return Number.isNaN(d.getTime()) ? "-" : d.toLocaleString();
    };

    const columns = useMemo(
        () => [
            { header: "Label", accessorKey: "label", size: 150 },
            {
                header: "BDN OCR ID",
                id: "erpDocumentId",
                accessorFn: (row) => row.erpDocumentId ?? row.ErpDocumentId,
                size: 280,
            },
            {
                header: "Report ID",
                id: "reportId",
                accessorFn: (row) => row.reportId ?? row.ReportId,
                size: 160,
            },
            {
                header: "WCN",
                id: "wcn",
                accessorFn: (row) => row.wcn ?? row.Wcn ?? "-",
                size: 120,
            },
            {
                header: "IMO",
                id: "imo",
                accessorFn: (row) => row.imo ?? row.Imo ?? "-",
                size: 120,
            },
            {
                header: "Document Type",
                id: "documentType",
                accessorFn: (row) => row.documentType ?? row.DocumentType ?? "-",
                size: 150,
            },
            {
                header: "File Name",
                id: "fileName",
                accessorFn: (row) => row.fileName ?? row.FileName,
                size: 300,
                Cell: ({ cell }) => (
                    <span className="ept-wrap-cell">{cell.getValue()}</span>
                ),
            },
            {
                header: "Last Error",
                id: "lastError",
                accessorFn: (row) => row.lastError ?? row.LastError ?? "-",
                size: 360,
                Cell: ({ cell }) => (
                    <span className="ept-error-cell ept-wrap-cell">
                        {cell.getValue()}
                    </span>
                ),
            },
            {
                header: "Last Attempt",
                id: "lastAttemptDate",
                accessorFn: (row) =>
                    formatDate(row.lastAttemptDate ?? row.LastAttemptDate),
                size: 180,
            },
            {
                header: "Actions",
                id: "actions",
                enableSorting: false,
                enableColumnFilter: false,
                size: 180,
                Cell: ({ row }) => {
                    const doc = row.original;
                    const erpId = doc.erpDocumentId ?? doc.ErpDocumentId;
                    const isRetrying = retrying.has(erpId);
                    return (
                        <Button
                            onClick={() => !isRetrying && handleRetry(doc)}
                            disabled={isRetrying}
                            primaryBackgroundColor={"#002A4E"}
                            primaryColor={"#002A4E"}
                        >
                            <span className="d-inline-flex align-items-center">
                                {isRetrying && (
                                    <span
                                        className="spinner-border spinner-border-sm me-2"
                                        role="status"
                                        aria-hidden="true"
                                    />
                                )}
                                {isRetrying ? "Retrying..." : "Retry publishing"}
                            </span>
                        </Button>
                    );
                },
            },
        ],
        [retrying]
    );

    const table = useMaterialReactTable({
        columns,
        data: documents,
        enableRowSelection: false,
        enableStickyHeader: true,
        enableColumnActions: false,
        enableColumnFilters: false,
        enableGlobalFilter: false,
        enableDensityToggle: false,
        enableFullScreenToggle: false,
        enablePagination: false,
        enableSorting: false,
        initialState: {
            density: "compact",
        },
        muiTableContainerProps: { sx: { maxHeight: "calc(100vh - 420px)" } },
        muiTableHeadCellProps: {
            sx: {
                color: "rgb(255 255 255)",
                "& .MuiButtonBase-root": {
                    color: "white !important",
                    "& .MuiTableSortLabel-icon": { color: "white !important" },
                },
            },
        },
        muiTableHeadRowProps: {
            sx: {
                backgroundColor: "#042a4c !important",
                "& .MuiTableCell-head::before": {
                    backgroundColor: "#042a4c !important",
                },
            },
        },
        muiTablePaperProps: {
            sx: { boxShadow: "0 0 8px 2px rgba(0, 0, 0, 0.1)" },
        },
        state: {
            isLoading: loading,
        },
    });

    const {
        userHasFeature,
        userFeaturesAreLoaded,
        fetchUserFeatures,
    } = useContext(ClaimsContext);

    useEffect(() => {
        if (!userFeaturesAreLoaded) {
            fetchUserFeatures();
        }
    }, [userFeaturesAreLoaded, fetchUserFeatures]);

    const isFeatureEnabled = useMemo(
        () => userHasFeature(FeatureType.ExternalDocumentPublishing),
        [userHasFeature]
    );

    if (userFeaturesAreLoaded && !isFeatureEnabled) {
        return null;
    }

    return (
        <MDBContainer fluid className="px-2 px-md-3 py-4 external-publishing-tracker">
            <MDBRow className="gx-2">
                <MDBCol md="12" lg="12">
                    <MDBCard className="h-100">
                        <MDBCardBody>
                            {/* Header */}
                            <div className="d-flex align-items-center justify-content-between mb-3">
                                <MDBCardTitle className="mb-0">
                                    BDN OCR Publishing Tracker
                                </MDBCardTitle>
                            </div>

                            {/* Filters */}
                            <div className="ept-filters d-flex flex-wrap mb-3">
                                <div className="ept-filter-field">
                                    <MDBInput
                                        label="BDN OCR ID"
                                        type="number"
                                        value={documentMstId}
                                        onChange={(e) => setDocumentMstId(e.target.value)}
                                    />
                                </div>
                                <div className="ept-filter-field">
                                    <MDBInput
                                        label="Report ID"
                                        type="number"
                                        value={reportMstId}
                                        onChange={(e) => setReportMstId(e.target.value)}
                                    />
                                </div>
                                <div className="ept-filter-actions">
                                    <Button
                                        onClick={handleSearch}
                                        disabled={loading}
                                        primaryBackgroundColor={COLORS.RED.DARK}
                                        primaryColor={COLORS.RED.DARK}
                                    >
                                        Search
                                    </Button>
                                    <Button
                                        className="ept-clear-btn"
                                        onClick={handleClear}
                                        disabled={loading}
                                    >
                                        Clear
                                    </Button>
                                </div>
                            </div>

                            {/* Table */}
                            <div className="list-height">
                                <MaterialReactTable table={table} />
                            </div>

                            {/* Pagination */}
                            <div className="ept-pagination d-flex align-items-center mt-3">
                                <Button
                                    onClick={handlePrev}
                                    disabled={!pagination?.isPreviousPageExists || loading}
                                >
                                    Previous
                                </Button>
                                <span className="ept-page-indicator mx-3">
                                    Page {pagination?.currentPage ?? page}
                                    {pagination?.totalPages
                                        ? ` of ${pagination.totalPages}`
                                        : ""}
                                </span>
                                <Button
                                    onClick={handleNext}
                                    disabled={!pagination?.isNextPageExists || loading}
                                >
                                    Next
                                </Button>
                            </div>
                        </MDBCardBody>
                    </MDBCard>
                </MDBCol>
            </MDBRow>
        </MDBContainer>
    );
};

export default ExternalPublishingTracker;